import { Layer, LayerType } from '@/orrm/model/layer'
import Wrapper from '@/orrm/wrapper/wrapper'
import TileSource from 'ol/source/Tile'
import { Source, TileWMS } from 'ol/source'
import TileLayer from 'ol/layer/Tile'
import OlLayer from 'ol/layer/Layer'
import { defaultProjection, getResolutionFromScale } from '@/utils/map'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS'
import { Style } from 'ol/style'
import { bbox as bboxStrategy } from 'ol/loadingstrategy'
import * as SLDReader from '@nieuwlandgeo/sldreader'
import { GeoJSON } from 'ol/format'
import { StyleLike } from 'ol/style/Style'

export default class LayerWrapper extends Wrapper<Layer> {
  opacity = 100
  enabled = true
  olLayer: OlLayer<any>

  constructor (origin: Layer, style?: StyleLike) {
    super(origin)

    const tileTypes: LayerType[] = ['WMS', 'WMTS']
    const vectorTypes: LayerType[] = ['WFS', 'fake']
    const asyncSource = this.buildLayerSource(origin)

    if (tileTypes.indexOf(origin.layerType) !== -1) {
      this.olLayer = new TileLayer({
        minResolution: origin.resolutionMin ? getResolutionFromScale(defaultProjection.getUnits(), origin.resolutionMin) : undefined,
        maxResolution: origin.resolutionMax ? getResolutionFromScale(defaultProjection.getUnits(), origin.resolutionMax) : undefined
      })

      asyncSource.then(source => {
        this.olLayer.setSource(source as TileSource)
      })
    } else if (vectorTypes.indexOf(origin.layerType) !== -1) {
      if (origin.sld) {
        style = this.buildStyle(origin) || style
      }

      this.olLayer = new VectorLayer({
        style
      })
      asyncSource.then(source => {
        this.olLayer.setSource(source as VectorSource<any>)
      })
    }

    this.olLayer.setProperties({
      getRubric: () => {
        return this.model.rubric
      }
    })
  }

  protected async buildLayerSource (layer: Layer): Promise<Source | null> {
    const inputParams = layer.params ? JSON.parse(layer.params) || {} : {}
    const guessedParams = this.guessParams(layer.flux?.value || '')
    const params = { ...guessedParams, ...inputParams }
    const flux = layer.flux?.value?.split('?')[0]

    if (layer.layerType === 'WMS') {
      const source = new TileWMS({
        url: flux,
        params: params
      })
      source.setProperties({ title: layer.title })
      return source
    } else if (layer.layerType === 'WMTS') {
      const capabilities = await layer.getCapabilities()
      const wmtsOptions = optionsFromCapabilities(capabilities, this.sanitizeKeys(params, this.WMTSKeysSanitizer))
      const source = new WMTS(wmtsOptions)
      source.setProperties({ title: layer.title })
      return source
    } else if (layer.layerType === 'WFS') {
      const Format = layer.OlWFSFormat || GeoJSON
      const source = new VectorSource({
        format: new Format(),
        url: (extent) => {
          const queryString = this.objectToQueryStringParams(params)
          const base = `${flux}?service=WFS&request=GetFeature&bbox=${extent.join(',')},${defaultProjection.getCode()}`
          return queryString ? `${base}&${queryString}` : base
        },
        strategy: bboxStrategy
      })
      source.setProperties({ title: layer.title })
      return source
    } else if (layer.layerType === 'fake') {
      return new VectorSource()
    }
    return null
  }

  public guessParams (url: string) {
    const split = url.split('?')
    if (split.length < 2) return {}
    split.shift()
    const queryParams = split.join('?').split('&')
    const params: { [key: string]: string } = {}
    for (const queryParam of queryParams) {
      const parts = queryParam.split('=')
      params[parts[0]] = parts[1]
    }
    return params
  }

  protected WMTSKeysSanitizer (key: string): string {
    const options = ['layer', 'matrixSet', 'projection', 'requestEncoding', 'style', 'format', 'crossOrigin']

    for (const option of options) {
      if (key.toLowerCase() === option.toLowerCase()) return option
    }
    return key
  }

  protected sanitizeKeys (obj: { [key: string]: any }, sanitizer: (key: string) => string) {
    const newObj: { [key: string]: any } = {}

    for (const key of Object.keys(obj)) {
      newObj[sanitizer(key)] = obj[key]
    }
    return newObj
  }

  protected buildStyle (layer: Layer): Style | undefined {
    const sldObject = SLDReader.Reader(layer.sld as string)
    const sldLayer = SLDReader.getLayer(sldObject)

    const defaultStyle = sldLayer.styles.find(style => style.default)
    if (defaultStyle) return SLDReader.createOlStyleFunction(defaultStyle.featuretypestyles.length ? defaultStyle.featuretypestyles[0] : [])
    if (sldLayer.styles.length > 0) return SLDReader.createOlStyleFunction(sldLayer.styles[0].featuretypestyles.length ? sldLayer.styles[0].featuretypestyles[0] : [])
    return undefined
  }

  protected objectToQueryStringParams (obj: { [key: string]: string }): string {
    const params: string[] = []

    for (const key in obj) {
      params.push(`${key}=${obj[key]}`)
    }
    return params.join('&')
  }
}
